import React, { useMemo } from "react";
import AvatarAndItemName from "~/components/items/AvatarAndItemName.jsx";
import Link from "next/link";
import { getShipmentBarcode, getShipmentCarrier, getShipmentCarrierId, getShipmentForeignId, getShipmentTrackingReference, getShipmentTrackingUrl } from "~/cores/shipmentCore";
import { getCarrierData } from "~/cores/appCore";
import { ShipmentBookingStatusChip } from "~/components/chips/statusChips/ShipmentBookingStatusChip.jsx";
import { ShipmentDeliveryStatusChip } from "~/components/chips/statusChips/ShipmentDeliveryStatusChip.jsx";
import { faTruck } from "@fortawesome/pro-regular-svg-icons";
import { CustomChip } from "~/components/chips/CustomChip.jsx";
import CollapsibleReturnPart from "./_components/CollapsibleReturnPart";
import { getShopAddressLine } from "~/cores/appCore";
import { COLORS } from "rylak-common/src/constants";
import { CopyOnClick } from "~/components/copyText/CopyOnClick.jsx";
import ExternalLink from "~/components/ExternalLink.jsx";
import LineDivider from "rylak-common/src/components/divider/LineDivider";
import CarrierIcon from "rylak-common/src/icons/CarrierIcon.jsx";
import { formatReadableDateAndTimeShort } from "rylak-common";
import { getTurnrShipmentHref } from "~/cores/pageAccessCore";

function Divider() {
    return (
        <div style={{ gridColumn: "span 2", visibility: "hidden" }}>
            <LineDivider />
        </div>
    );
}

function DataRow({ label, value, copyOnClick = false, copyValue = value }) {
    if (!value) {
        return null;
    }
    return (
        <>
            <div style={{ color: COLORS.WEAK_TEXT }}>{label}:</div>
            <div style={{ width: "fit-content" }}>{copyOnClick ? <CopyOnClick text={copyValue}>{value}</CopyOnClick> : value}</div>
        </>
    );
}

function HeaderChip({ shipment, carrierData }) {
    return (
        <div
            style={{
                display: "flex",
                fontSize: "12px",
                alignItems: "center",
            }}
        >
            <Link
                href={getTurnrShipmentHref({
                    shipment,
                })}
            >
                {shipment?.id}
            </Link>
            <div style={{ marginLeft: "12px", marginRight: "10px" }}>
                <CarrierIcon carrierData={carrierData} />
            </div>
        </div>
    );
}

export function ShipmentCard(props) {
    const { appProps, shipment, onUpdate } = props;

    const statusChips = useMemo(() => {
        const chips = [<ShipmentBookingStatusChip {...props} key={"1"} entity={shipment} showLabel={true} size={"small"} onSuccess={onUpdate} toolTipPlacement="top" />];
        if (shipment?.deliveryStatus) {
            chips.push(<ShipmentDeliveryStatusChip status={shipment?.deliveryStatus} key={"2"} showLabel={true} size={"small"} onSuccess={onUpdate} toolTipPlacement="top" />);
        }
        return chips;
    }, [shipment, onUpdate]);

    return (
        <CollapsibleReturnPart
            appProps={appProps}
            headerIcon={faTruck}
            headerTitle={"Shipment:"}
            headerChip={getShipmentCarrier(shipment) && <HeaderChip shipment={shipment} carrierData={getCarrierData(appProps, { id: getShipmentCarrierId(shipment) })} />}
            statusChips={statusChips}
            dateChip={shipment?.bookedAt && <CustomChip tooltipTitle={"Booked at"} label={formatReadableDateAndTimeShort({ dateTime: shipment?.bookedAt })}></CustomChip>}
            optionsEntity={shipment}
            onUpdate={onUpdate}
        >
            <div
                style={{
                    fontSize: "12px",
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "20px",
                    rowGap: "10px",
                    alignItems: "center",
                }}
            >
                <DataRow label={"Foreign id"} value={getShipmentForeignId(shipment)} copyOnClick={true} />
                <DataRow label={"Tracking ref"} value={getShipmentTrackingReference(shipment)} copyOnClick={true} />
                <DataRow
                    label={"Tracking url"}
                    value={getShipmentTrackingUrl(shipment) && <ExternalLink href={getShipmentTrackingUrl(shipment)} iconSize={10} />}
                    copyValue={getShipmentTrackingUrl(shipment)}
                    copyOnClick={true}
                />
                <DataRow label={"Barcode"} value={getShipmentBarcode(shipment)} copyOnClick={true} />
                <Divider />
                <DataRow label={"From"} value={<span style={{ color: COLORS.WEAK_TEXT }}>{getShopAddressLine(shipment?.sender)}</span>} />
                <DataRow label={"To"} value={<span style={{ color: COLORS.WEAK_TEXT }}>{getShopAddressLine(shipment?.recipient)}</span>} />
            </div>
            <LineDivider className={"mt3"} />
            <div className={"mt3"} style={{ display: "grid", gap: "15px" }}>
                {shipment?.items?.map((item, index) => {
                    return <AvatarAndItemName orderItem={item} key={index} />;
                })}
            </div>
        </CollapsibleReturnPart>
    );
}
